import {
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Tooltip,
} from "@mui/material";
import { PREEXEC_ACTION_KINDS, RAW_DATASET_TYPES } from "../../constants";
import {
    getMissionLocalFolderPath,
    getMissionLocalFolderPathFromRelations,
} from "../alignment-report/getMissionLocalFolderPath";
import { useCallback, useMemo } from "react";

import { BACKEND_ROUTES } from "../../backendRoutes";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { DataGrid } from "@mui/x-data-grid";
import { MODEL_PROPTYPES } from "../../models";
import PropTypes from "prop-types";
import { camelCaseToTitleCase } from "../../utils";
import { generateCommand } from "../../utils/generateCommand";
import { get } from "../../utils/fetchUtils";
import useSWR from "swr";
import { useSnackbar } from "../../hooks";

const rawDatasetTypes = Object.values(RAW_DATASET_TYPES);

/**
 * Fetch the raw dataset count of every RAW_DATASET_TYPES value
 * for a mission and return them keyed by type.
 */
const fetchCountByType = async ([, missionUuid]) => {
    const results = await Promise.all(
        rawDatasetTypes.map((type) =>
            get(
                `${BACKEND_ROUTES.RAW_DATASET}?missionUuid=${missionUuid}&type=${type}&limit=0`
            )
        )
    );

    return Object.fromEntries(
        rawDatasetTypes.map((type, index) => [type, results[index]?.count ?? 0])
    );
};

function useRawDatasetCountByType(missionUuid) {
    const { data, isLoading } = useSWR(
        missionUuid ? ["rawDatasetCountByType", missionUuid] : null,
        fetchCountByType
    );
    return { countByType: data, isLoading };
}

export function RawDatasetTypeList({ missionUuid, mission }) {
    const { openSnackbar } = useSnackbar();

    const { countByType, isLoading } = useRawDatasetCountByType(missionUuid);

    const outputFolder = useMemo(
        () =>
            mission
                ? (getMissionLocalFolderPath(mission) ??
                  getMissionLocalFolderPathFromRelations(mission) ??
                  ".")
                : ".",
        [mission]
    );

    const rows = useMemo(
        () =>
            rawDatasetTypes
                .filter((type) => countByType?.[type] > 0)
                .map((type) => ({
                    id: type,
                    typeName: camelCaseToTitleCase(type),
                    count: countByType[type],
                    type,
                })),
        [countByType]
    );

    const handleCopyCommand = useCallback(
        async (type) => {
            const spec = {
                [type]: {
                    kind: PREEXEC_ACTION_KINDS.DOWNLOAD_FILES,
                    endpoint: "/raw-datasets",
                    args: { missionUuid, type, quarantine: false },
                    from: "s3Location",
                    to: "destination",
                },
                outputFolder,
            };

            try {
                await navigator.clipboard.writeText(
                    generateCommand(spec, "pre")
                );
                openSnackbar("Download command copied", "success");
            } catch (error) {
                openSnackbar(error.message, "error");
            }
        },
        [missionUuid, outputFolder, openSnackbar]
    );

    const columns = useMemo(
        () => [
            {
                field: "typeName",
                headerName: "Type",
                minWidth: 200,
                flex: 2,
                sortable: false,
            },
            {
                field: "count",
                headerName: "Count",
                minWidth: 100,
                flex: 1,
                sortable: false,
            },
            {
                field: "actions",
                headerName: "",
                minWidth: 60,
                flex: 0.5,
                sortable: false,
                renderCell: ({ row }) => (
                    <Tooltip title="Copy download command">
                        <IconButton
                            size="small"
                            onClick={() => handleCopyCommand(row.type)}
                        >
                            <ContentCopyIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                ),
            },
        ],
        [handleCopyCommand]
    );

    if (!isLoading && rows.length === 0) return null;

    return (
        <Card>
            <CardHeader title="Raw Datasets" />
            <CardContent>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={isLoading}
                    hideFooter
                    disableColumnMenu
                    sx={{ "--DataGrid-overlayHeight": "100px" }}
                />
            </CardContent>
        </Card>
    );
}

RawDatasetTypeList.propTypes = {
    missionUuid: PropTypes.string.isRequired,
    mission: PropTypes.shape(MODEL_PROPTYPES.Mission),
};
